import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { casesApi, getDocTypeLabel } from "../api.js";
import { StatusBadge } from "../components/StatusBadge.js";
import { Modal } from "../components/Modal.js";
import { toast } from "../components/Toast.js";
import { btnStyle } from "../App.js";
import { downloadIssuedPdf } from "../lib/downloadPdf.js";
import { startDocQueue } from "../lib/documentQueue.js";
import { WorkItemsSection } from "../components/work-items/WorkItemsSection.js";
import { GenerateDocumentsModal } from "../components/work-items/GenerateDocumentsModal.js";
import { CreateDocumentsModal } from "../components/document-create/CreateDocumentsModal.js";
import { workItemsApi } from "../api/workItemsApi.js";
import { useScreenView } from "../lib/analytics.js";
import { useTenantDocumentTypes } from "../hooks/useDocTypeProfession.js";

type CreateStep = "menu" | "from-work-items";

export function CasePage() {
  useScreenView("case_detail");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // 書類作成フローの段階。null=閉、"menu"=作成方法メニュー（CreateDocumentsModal）、
  // "from-work-items"=実施業務から生成（GenerateDocumentsModal）
  const [createStep, setCreateStep] = useState<CreateStep | null>(null);
  // 発行済みPDFのダウンロード中の書類ID（ボタンの二度押し防止）
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  // 案件編集モーダル（Issue 8: 案件名・備考の編集UIが存在しなかった）
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", notes: "" });

  async function handleDownloadPdf(docId: string, docNumber: string | null) {
    setDownloadingId(docId);
    try {
      const filename = await downloadIssuedPdf(docId, docNumber);
      toast(`${filename} をダウンロードしました`);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setDownloadingId(null);
    }
  }

  const { data: caseDetail, isLoading } = useQuery({
    queryKey: ["case", id],
    queryFn: () => casesApi.get(id!),
    enabled: !!id,
  });

  // estimate-invoice-sync §3.5: 見積書↔請求書の同期状態
  const { data: estimateStatus } = useQuery({
    queryKey: ["case", id, "estimate-status"],
    queryFn: () => casesApi.estimateStatus(id!),
    enabled: !!id,
  });

  const { data: workItemsData } = useQuery({
    queryKey: ["case", id, "work-items"],
    queryFn: () => workItemsApi.list(id!),
    enabled: !!id,
  });

  const { data: workItemsSummary } = useQuery({
    queryKey: ["case", id, "work-items", "summary"],
    queryFn: () => workItemsApi.summary(id!),
    enabled: !!id,
  });

  // カスタム書類種別（tenant_document_types）のラベルを一覧表示で解決するための共通クエリ
  // （reviewer 指摘: 一覧が16進数のdoc_typeキーのまま表示されていた問題の対応）
  const { data: docTypesData } = useTenantDocumentTypes();
  function docTypeLabel(docType: string): string {
    return docTypesData?.data.find((d) => d.doc_type === docType)?.label ?? getDocTypeLabel(docType);
  }

  const updateMut = useMutation({
    mutationFn: () => casesApi.update(id!, editForm),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["case", id] });
      setEditing(false);
      toast("案件を更新しました");
    },
    onError: (e) => toast(e.message, "error"),
  });

  function openEdit() {
    if (!caseDetail) return;
    setEditForm({ title: caseDetail.title ?? "", notes: caseDetail.notes ?? "" });
    setEditing(true);
  }

  if (isLoading) return <div style={{ padding: 24, color: "#94a3b8" }}>読み込み中…</div>;
  if (!caseDetail) return <div style={{ padding: 24, color: "#dc2626" }}>案件が見つかりません</div>;

  const docs = caseDetail.documents ?? [];
  const workItemCount = workItemsData?.total ?? 0;
  const hasDraftDocs = docs.some((doc) => doc.status === "draft");
  const hasIssuedDocs = docs.some((doc) => doc.status === "issued");

  return (
    <div style={{ padding: 24 }}>
      {/* パンくず */}
      <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>
        <Link to="/clients" style={{ color: "#0f766e", textDecoration: "none" }}>顧客管理</Link>
        {" › "}
        <Link to={`/clients/${caseDetail.client_id}`} style={{ color: "#0f766e", textDecoration: "none" }}>
          {caseDetail.client_name}
        </Link>
        {" › "}{caseDetail.title ?? `令和${caseDetail.fiscal_year}年 案件`}
      </div>

      {/* 案件ヘッダー */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20, marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <h1 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>
              {caseDetail.title
                ? `${caseDetail.client_name}　${caseDetail.title}`
                : `${caseDetail.client_name}　令和${caseDetail.fiscal_year}年`}
            </h1>
            <StatusBadge status={caseDetail.status} />
            <span style={{ fontSize: 12, color: "#94a3b8" }}>令和{caseDetail.fiscal_year}年</span>
          </div>
          <button style={btnStyle("outline")} onClick={openEdit}>編集</button>
        </div>
        {caseDetail.notes && <p style={{ fontSize: 13, color: "#475569", margin: 0 }}>{caseDetail.notes}</p>}
      </div>

      {editing && (
        <Modal title="案件を編集" onClose={() => setEditing(false)}>
          <form
            onSubmit={(e) => { e.preventDefault(); updateMut.mutate(); }}
            style={{ display: "flex", flexDirection: "column", gap: 14 }}
          >
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>案件名</span>
              <input
                type="text"
                value={editForm.title}
                placeholder={`例：令和${caseDetail.fiscal_year}年 案件`}
                onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                style={{ padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14 }}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>備考</span>
              <input
                type="text"
                value={editForm.notes}
                placeholder="連絡事項など"
                onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                style={{ padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14 }}
              />
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
              <button type="button" style={btnStyle("outline")} onClick={() => setEditing(false)}>キャンセル</button>
              <button type="submit" style={btnStyle("primary")} disabled={updateMut.isPending}>更新する</button>
            </div>
          </form>
        </Modal>
      )}

      <FlowSteps
        workItemCount={workItemCount}
        hasDraftDocs={hasDraftDocs}
        hasIssuedDocs={hasIssuedDocs}
      />

      {/* 実施業務セクション（書類一覧より上） */}
      {id && <WorkItemsSection caseId={id} />}

      {/* 書類一覧 */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>書類一覧</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            style={btnStyle("primary")}
            onClick={() => setCreateStep("menu")}
            title="作成方法を選んで書類（下書き）を作ります"
          >
            📄 書類を作成
          </button>
        </div>
      </div>

      {docs.length === 0 ? (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "32px 24px", textAlign: "center", color: "#94a3b8" }}>
          まだ書類がありません。「📄 書類を作成」から作成方法を選んでください。
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
              {["書類番号", "種別", "バージョン", "ステータス", "発行日", ""].map((h) => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: "#64748b", fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "10px 12px", fontWeight: 600, color: "#0f172a" }}>
                  {d.doc_number ?? "—（未発行）"}
                </td>
                <td style={{ padding: "10px 12px" }}>
                  {docTypeLabel(d.doc_type)}
                  {d.doc_type === "estimate" && d.status === "draft" && estimateStatus?.estimate?.id === d.id && (
                    estimateStatus.sync === "stale" ? (
                      <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: "#92400e", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 4, padding: "2px 6px" }}>
                        ⚠ 請求書と差分あり
                      </span>
                    ) : estimateStatus.sync === "in_sync" ? (
                      <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: "#134e4a", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 4, padding: "2px 6px" }}>
                        ✓ 請求書と一致
                      </span>
                    ) : null
                  )}
                </td>
                <td style={{ padding: "10px 12px", color: "#64748b" }}>v{d.version}</td>
                <td style={{ padding: "10px 12px" }}><StatusBadge status={d.status} /></td>
                <td style={{ padding: "10px 12px", color: "#94a3b8", fontSize: 12 }}>
                  {d.issued_at ? new Date(d.issued_at).toLocaleDateString("ja-JP") : "—"}
                </td>
                <td style={{ padding: "10px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                  <div style={{ display: "inline-flex", gap: 8 }}>
                    {d.status === "issued" && (
                      <button
                        style={btnStyle("primary")}
                        disabled={downloadingId === d.id}
                        onClick={() => handleDownloadPdf(d.id, d.doc_number)}
                      >
                        {downloadingId === d.id ? "取得中…" : "PDFダウンロード"}
                      </button>
                    )}
                    <button style={btnStyle("outline")} onClick={() => navigate(`/documents/${d.id}`)}>開く</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ── ① 作成方法を選ぶメニュー ──────────────────────────────── */}
      {createStep === "menu" && id && (
        <CreateDocumentsModal
          caseId={id}
          docs={docs}
          workItemCount={workItemCount}
          estimateStatus={estimateStatus}
          onClose={() => setCreateStep(null)}
          onSelectWorkItems={() => setCreateStep("from-work-items")}
        />
      )}

      {/* ── ② 実施業務から書類を生成（既存のモーダルを再利用） ───── */}
      {createStep === "from-work-items" && workItemsSummary && id && (
        <GenerateDocumentsModal
          caseId={id}
          summary={workItemsSummary}
          onClose={() => setCreateStep(null)}
          onSuccess={(docIds) => {
            setCreateStep(null);
            qc.invalidateQueries({ queryKey: ["case", id] });
            // 一括作成した下書きを「仕上げキュー」に積み、書類画面で1件ずつ巡回できるようにする
            startDocQueue(id!, docIds);
            navigate(`/documents/${docIds[0]!}`);
          }}
        />
      )}
    </div>
  );
}

function FlowSteps({
  workItemCount,
  hasDraftDocs,
  hasIssuedDocs,
}: {
  workItemCount: number;
  hasDraftDocs: boolean;
  hasIssuedDocs: boolean;
}) {
  const steps = [
    { label: "業務登録", done: workItemCount > 0, current: workItemCount === 0 },
    { label: "書類生成", done: hasDraftDocs || hasIssuedDocs, current: workItemCount > 0 && !hasDraftDocs && !hasIssuedDocs },
    { label: "内容確認・編集", done: hasIssuedDocs, current: hasDraftDocs && !hasIssuedDocs },
    { label: "PDF発行", done: hasIssuedDocs, current: false },
  ];

  return (
    <section style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 14, marginBottom: 24 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8 }}>
        {steps.map((step, index) => (
          <div
            key={step.label}
            style={{
              border: `1px solid ${step.done ? "#99f6e4" : step.current ? "#facc15" : "#e2e8f0"}`,
              background: step.done ? "#f0fdfa" : step.current ? "#fffbeb" : "#f8fafc",
              borderRadius: 8,
              padding: "10px 12px",
              minWidth: 0,
            }}
          >
            <div style={{ fontSize: 11, color: step.done ? "#0f766e" : step.current ? "#92400e" : "#64748b", fontWeight: 900 }}>
              {index + 1}
            </div>
            <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 800, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {step.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
